import { FunctionComponent, useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Daycare } from '@/types/Daycare'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import LevelRow from './levelsTableRow'
import styles from './levels.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import Title from '@/components/_custom/Title/title'
import { GET_STAFF_LEVELS } from '@/modules/API/staff/staffLevels'
import { StaffLevel } from '@/types/StaffLevel'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import Loading from '@/components/Loading/Loading'
import StaffLevelDialog from './Dialog/staffLevelDialog'
import { useAuth } from '@/modules/context/AuthContext'

interface LevelsProps {
    daycare: Daycare
    canUpdate: boolean
}

const Levels: FunctionComponent<LevelsProps> = ({ daycare, canUpdate }: LevelsProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const [levelsList, setLevelsList] = useState<Array<StaffLevel>>([])

    const [isLevelDialogOpen, setLevelDialogOpen] = useState<boolean>(false)

    const {
        loading: loadingStaffLevels,
        error: errorStaffLevels,
        data: dataStaffLevels
    } = useQuery<{
        getStaffLevels: Array<StaffLevel>
    }>(GET_STAFF_LEVELS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycare.id
        }
    })

    useEffect(() => {
        if (dataStaffLevels) {
            setLevelsList(
                [...dataStaffLevels.getStaffLevels].sort((a: StaffLevel, b: StaffLevel) => {
                    if (a.required && !b.required) return -1
                    if (!a.required && b.required) return 1
                    return a.name.localeCompare(b.name)
                })
            )
        }
    }, [dataStaffLevels])

    function addOneStaffLevel(section: StaffLevel): void {
        const newLevelsList: StaffLevel[] = [...levelsList, section].sort((a: StaffLevel, b: StaffLevel) => {
            if (a.required && !b.required) return -1
            if (!a.required && b.required) return 1
            return a.name.localeCompare(b.name)
        })
        setLevelsList([])
        setLevelsList(newLevelsList)
        fetchCurrentDaycare()
    }

    function removeOneStaffLevel(levelId: number): void {
        const newList: StaffLevel[] = levelsList.filter((level: StaffLevel) => level.id !== levelId)
        setLevelsList([])
        setLevelsList(newList)
        fetchCurrentDaycare()
    }

    function updateOneStaffLevel(section: StaffLevel): void {
        const sectionList: StaffLevel[] = levelsList.filter((s: StaffLevel): boolean => s.id !== section.id)
        setLevelsList([])
        setLevelsList(
            [...sectionList, section].sort((a: StaffLevel, b: StaffLevel) => {
                if (a.required && !b.required) return -1
                if (!a.required && b.required) return 1
                return a.name.localeCompare(b.name)
            })
        )
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.LevelPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myStaffLevels')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setLevelDialogOpen(true)}
                        />
                    )}
                </div>
                {loadingStaffLevels && <Loading overall={false} />}
                {dataStaffLevels && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell sx={{ width: '70%' }}>{t('name')}</TableCell>
                                        <TableCell sx={{ width: '20%' }} align={'center'}>
                                            {t('required')}
                                        </TableCell>
                                        {canUpdate && (
                                            <TableCell colSpan={2} sx={{ width: '10%' }} align={'center'}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {levelsList.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3}>{t('noStaffLevels')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        levelsList.map((level, index) => (
                                            <LevelRow
                                                key={level.id + level.name + index}
                                                level={level}
                                                daycare={daycare}
                                                canUpdate={canUpdate}
                                                removeOneStaffLevel={removeOneStaffLevel}
                                                updateOneStaffLevel={updateOneStaffLevel}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <StaffLevelDialog
                            open={isLevelDialogOpen}
                            handleClose={() => setLevelDialogOpen(false)}
                            create={true}
                            addOneStaffLevel={addOneStaffLevel}
                            daycare={daycare}
                        />
                    </>
                )}
                {errorStaffLevels && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default Levels
